import { deleteRequest, get, post, put, patch } from 'helpers/api';
import { put as turboPut } from 'helpers/turbo_api';
import { snakeCase } from 'lodash';
import type {
  CallList,
  CallListImportStatus,
  CallListItem,
  CallTemplate,
  ConnectProperty,
  Contact,
  CreateAddressImportResponse,
  DataExportTemplateResponse,
  DemographicsReport,
  ExportCreditCounts,
  Feedback,
  FetchCallListItemsResponse,
  FetchLastFetchedAtResponse,
  GetCallConsoleItemResponse,
  InsightsProperty,
  Note,
  OwnedProperty,
  OwnershipsResponse,
  Profile,
  Region,
  PropertyCrmSync,
  SearchContactsResponse,
  TagResponse,
  TagsResponse,
  UpdateProfileParams,
  RethinkList,
  FetchMailersTemplatesResponse,
  CreateMailersTemplateResponse,
  PreviewMailerResponse,
  MailersHeader
} from '../types';
import type { CallListUser } from '../components/call_list/manage_call_list_users';

export const CALL_LISTS_INDEX = 'INDEX';
export const CALL_LISTS_NEXT = 'NEXT';
export const CALL_LISTS_PREVIOUS = 'PREVIOUS';
export const CALL_LISTS_SEARCH = 'SEARCH';
export const CALL_LISTS_SHOW = 'SHOW';
export const CALL_CONSOLE = 'CALL_CONSOLE';
export const CALL_LIST_EXPORT = 'CALL_LIST_EXPORT';

export const offMarketPropertyUrl = (propertyId, fromConsole = false) => {
  if (fromConsole) {
    return `/prospect/off_market_properties/${propertyId}?from_console=true`;
  } else {
    return `/prospect/off_market_properties/${propertyId}`;
  }
};
export const exclusiveListingUrl = (listingId: number) => `/prospect/listings/${listingId}`;
export const dataExportDownloadUrl = (id: number) => `/prospect/data_exports/${id}/download`;
export const downloadMailersExport = (id: number) => `/prospect/mailers_exports/${id}/download`;
export const profileSubscriptionUrl = '/prospect/profile#subscription';
export const profileNotificationUrl = '/prospect/profile#notifications';
export const profileMailerExportsUrl = '/prospect/profile?export_tab=mailer_exports#exports';
export const profileDataExportsUrl = '/prospect/profile?export_tab=data_exports#exports';
export const profilePremiumSignupUrl = '/prospect/profile/premium_signup';
export const profilePremiumThankYouUrl = '/prospect/profile/premium_thank_you';
export const savedSearchesUrl = '/prospect/saved_searches';
export const downloadCompsExportUrl = (insightsPropertyId: string, insightsPropertyIds: string[]) => `/prospect/off_market_properties/${insightsPropertyId}/comps_export?insights_property_ids=${insightsPropertyIds}`;
export const viewSavedSearchPropertiesUrl = (savedSearchId: number) => `/prospect/search?search_criteria=${savedSearchId}`;
export const downloadSingleMailerUrl = (callListId: number, templateId: number, callListItemId: number, mailerType: 'pdf' | 'docx') => {
  return `/prospect/connect_call_lists/${callListId}/download_single_mailer?item_id=${callListItemId}&template_id=${templateId}&mailer_type=${mailerType}`;
};
export const mailersPreviewUrl = (callListId: number, templateId: number) => {
  return `/prospect/connect_call_lists/${callListId}/mailers_templates/${templateId}/preview`;
};
export const mailersTemplatesUrl = (callListId: number) => {
  return `/prospect/connect_call_lists/${callListId}/mailers_templates`;
};
export const mailersTemplateEditUrl = (callListId: number, templateId: number) => {
  return `/prospect/connect_call_lists/${callListId}/mailers_templates/${templateId}/edit`;
};
export const firstSearchUrl = '/prospect/search?first_search=true';

const CALL_LISTS_BASE_PATH = '/prospect/connect_call_lists';
export const callListUrl = (action: string, callListId?: number, index?: number) => {
  switch (action) {
  case CALL_LISTS_INDEX:
    return CALL_LISTS_BASE_PATH;
  case CALL_CONSOLE:
    return `${CALL_LISTS_BASE_PATH}/${callListId}/call_console`;
  case CALL_LISTS_NEXT:
  case CALL_LISTS_PREVIOUS:
    return `${CALL_LISTS_BASE_PATH}/${callListId}/call_console?index=${index}`;
  case CALL_LISTS_SEARCH:
    return '/prospect/search';
  case CALL_LISTS_SHOW:
    return `${CALL_LISTS_BASE_PATH}/${callListId}`;
  case CALL_LIST_EXPORT:
    return `${CALL_LISTS_BASE_PATH}/${callListId}/data_export`;
  default:
    return CALL_LISTS_BASE_PATH;
  }
};

export function fetchProfile(): Promise<Profile> {
  return get('/prospect/profile');
}

export function updateProfile(profile: UpdateProfileParams, source: string) {
  return put('/prospect/profile', { profile, source });
}

export function updateProspectAccount(prospectAccount: { regions_attributes?: Region[] }) {
  return put('/prospect/prospect_account', { prospectAccount });
}

export function getProspectAccountMembers(): Promise<CallListUser[]> {
  return get('/prospect/prospect_account/prospect_account_members');
}

export function subscriptionStatus(): Promise<string> {
  return get('/prospect/profile/subscription_status').then(({ status }) => status);
}

export function listingSearch(criteria, signal: AbortSignal) {
  return post('/prospect/search/search_all', { set: criteria }, { signal });
}

export function detailedListingSearch(criteria, page = 0, signal?: AbortSignal) {
  return post('/prospect/search/search', { set: criteria, page }, { signal });
}

export function findOffMarketProperty(propertyId: string, source?: string): Promise<InsightsProperty> {
  return get(`/prospect/off_market_properties/${propertyId}${source ? `?source=${source}` : ''}`);

}

export function findListing(listingId) {
  return get('/prospect/search/info', { params: { listingId } });
}

export function fetchOwners(insightsPropertyId: string,
  useCredit = false,
  force = false,
  includeAi = false
): Promise<OwnershipsResponse> {
  const url = `/prospect/off_market_properties/${insightsPropertyId}/ownerships`;
  return get(url, { params: { useCredit, force, includeAi } });
}

export function fetchRemainingCredits(): Promise<{ remainingCredits: number }> {
  return get(`/prospect/prospect_account/remaining_credits`);
}

export function createActivity(activity, callListId) {
  return post('/prospect/activities', { activity, callListId });
}

export function deleteActivity(activityId) {
  return deleteRequest(`/prospect/activities/${activityId}`);
}

export function updateActivity(activity) {
  return put(`/prospect/activities/${activity.id}`, { activity });
}

export function updateOwnership(insightsPropertyId, ownershipId, params) {
  return put(`/prospect/off_market_properties/${insightsPropertyId}/ownerships/${ownershipId}`, { ownership: params });
}

export function createOwnershipRequest(insightsPropertyId: string, contactId: number): Promise<OwnershipsResponse> {
  return post(`/prospect/off_market_properties/${insightsPropertyId}/ownerships`, { contactId });
}

export function deleteOwnershipRequest(insightsPropertyId: string, ownershipId: number) {
  return deleteRequest(`/prospect/off_market_properties/${insightsPropertyId}/ownerships/${ownershipId}`);
}

export function fetchLastFetchedAt(insightsPropertyId: string): Promise<FetchLastFetchedAtResponse> {
  return get(`/prospect/off_market_properties/${insightsPropertyId}/last_fetched`);
}

export function fetchContact(contactId: number): Promise<Contact> {
  return get(`/prospect/connect_contacts/${contactId}`);
}

export function fetchContactOwnedProperties(contactId: number): Promise<OwnedProperty[]> {
  return get(`/prospect/connect_contacts/${contactId}/owned_properties`);
}

export function zoneListForBounds(bounds) {
  return post('/prospect/off_market_properties/zone_list', { bounds });
}

export function updateContactRequest(contactId: number, params) {
  if (params.company && params.company.id) {
    params.companyId = params.company.id;
  }
  return put(`/prospect/connect_contacts/${contactId}`, { contact: params });
}

export function searchContacts(query: string,
  page?: number,
  sort?: { [key: string]: string },
  tags?: string[],
  interacted?: boolean
): Promise<SearchContactsResponse> {
  const params = {
    q: query,
    page,
    interacted: interacted || false,
    s: sort || undefined,
    tags: (tags && tags.length > 0) ? tags : undefined
  };
  return get(`/prospect/connect_contacts`, { params });
}

export function createContact(contact: Contact & { companyId: number }) {
  if (contact.company && contact.company.id) {
    contact.companyId = contact.company.id;
  }
  return post(`/prospect/connect_contacts`, { contact });
}

export function deleteContact(contactId: number) {
  return deleteRequest(`/prospect/connect_contacts/${contactId}`);
}

export function deleteContacts(contactIds: number[]) {
  return deleteRequest(`/prospect/connect_contacts/destroy_multiple`, { params: { ids: contactIds } });
}

export function importContacts(flatfileSheetId: string) {
  return post(`/prospect/connect_contacts/import`, { flatfileSheetId });
}

export function fetchNotes(recordId: number, recordType: string): Promise<Note[]> {
  return get(`/prospect/notes`, { params: { notableId: recordId, notableType: recordType } });
}

export function createNote(body: string,
  recordId?: number,
  recordType?: string,
  insightsPropertyId?: string,
  callListId?: number
): Promise<Note> {
  return post(`/prospect/notes`, {
    insightsPropertyId,
    note: { notableId: recordId, notableType: recordType, body, callListId, }
  });
}

export function deleteNote(noteId: number) {
  return deleteRequest(`/prospect/notes/${noteId}`);
}

export function updateNote(noteId: number, body: string): Promise<Note> {
  return put(`/prospect/notes/${noteId}`, { note: { body } });
}

export function evaluateTemplate(templateId, contactId, propertyId) {
  return get(`/prospect/liquid_templates/${templateId}/evaluate`, { params: { contactId, propertyId } });
}

export function myPropertiesByAddress(
  address: string,
  page = 1
): Promise<{ total: number, properties: ConnectProperty[] }> {
  return get(`/prospect/properties?q[address_cont]=${address}&page=${page}`);
}

export function updateSavedSearch(id: number, name: string) {
  return put(`/prospect/saved_searches/${id}`, { savedSearch: { name } });
}

export function deleteSavedSearch(id: number) {
  return deleteRequest(`/prospect/saved_searches/${id}`);
}

export function fetchExclusiveSearchCount(id: number) {
  return get(`/prospect/saved_searches/${id}/result_count`);
}


export function checkSavedSearchForNewProperties(id: number) {
  return post(`/prospect/saved_searches/${id}/check_for_new_properties`);
}

/**
 * Save a criteria to the backend.
 * @param {object} criteria - The criteria object from the search redux store
 * @param {string} name - The name to save the criteria as
 * @param {string} type - The type of criteria to save.
 *                        Either 'Connect::OffMarketSavedSearch' OR 'Connect::BuildoutSavedSearch'
 * @returns {Promise} The network request promise
 */
export function saveSearch(criteria, name: string, type: string, insightsPropertyIds?: string[]): Promise<void> {
  return post(
    '/prospect/search/save_search',
    {
      set: {
        ...criteria,
        locationsAttributes: locationAttributesFromCriteria(criteria),
        name: name ? name : undefined,
        type,
        insightsPropertyIds
      }
    }
  );
}

/**
 * Performs both the detailed search and the search to fetch all results.
 * This is faster than performing both individually because the criteriaToOffMarketSearchParams method involves
 * an network request to Clarity. This method only calls criteriaToOffMarketSearchParams once and then reuses
 * the resulting params in the subsequent calls to Buildout
 * @param {object} criteria - The criteria object from the search redux store
 * @param {number} detailedLimit - The limit for the detailed off market search results
 * @param {number} limit - The limit for the off market search results
 * @param {boolean} includeGeometry - Ask insights to include geometry in the property details
 * @returns {array} Array of network request promises [detailedResults, results]
 */
export async function bothOffMarketPropertySearchesFromCriteria(
  criteria, detailedLimit: number, limit: number, includeGeometry = false, signal: AbortSignal
) {
  const params = await criteriaToOffMarketSearchParams(criteria);
  params.includeGeometry = includeGeometry;

  return [
    detailedOffMarketPropertySearch(params, detailedLimit, 0, signal),
    offMarketPropertySearch(params, limit, signal)
  ];
}

export async function offMarketPropertySearchFromCriteria(
  criteria,
  limit: number,
  includeGeometry = false,
  signal: AbortSignal
) {
  const params = await criteriaToOffMarketSearchParams(criteria);
  params.includeGeometry = includeGeometry;

  return offMarketPropertySearch(params, limit, signal);
}

export async function detailedOffMarketPropertySearchFromCriteria(criteria, limit: number, signal: AbortSignal) {
  const params = await criteriaToOffMarketSearchParams(criteria);

  return detailedOffMarketPropertySearch(params, limit, 0, signal);
}

export function detailedOffMarketPropertySearch(params, limit: number, offset = 0, signal?: AbortSignal) {
  return post('/prospect/off_market_properties/search', { insightsProperty: { ...params, limit, offset } }, { signal });
}

export function offMarketPropertySearch(params, limit: number, signal?: AbortSignal) {
  return post('/prospect/off_market_properties/search_all', { insightsProperty: { ...params, limit } }, { signal });
}

async function criteriaToOffMarketSearchParams(criteria) {
  const params = {
    ...criteria,
    mapBounds: criteria.mapBounds || []
  };

  return params;
}

export function propertyIdOffMarketPropertySearch(address: string, city: string, state: string) {
  return post('/prospect/off_market_properties/search_property_id_by_address', {
    address: address,
    city: city,
    state: state,
  });
}

export function propertiesByAddress(address: string) {
  return post('/prospect/off_market_properties/search_properties_by_address', {
    address: address
  });
}

export function offMarketAssociatedPropertiesCount(insightsPropertyId: string): Promise<number> {
  return get(`/prospect/off_market_properties/${insightsPropertyId}/associated_properties_count`);
}

/**
 * When saving a criteria to the backend, save the mapBounds as the location if no other locations are present.
 * We do this by transforming the map bounds into a valid location polygon.
 */
function locationAttributesFromCriteria(criteria) {
  if (criteria.locationsAttributes && criteria.locationsAttributes.length > 0) return criteria.locationsAttributes;

  if (criteria.mapBounds) return [{
    name: 'Map View',
    polygonSearch: true,
    polygonGeojson: {
      type: 'Polygon',
      coordinates: [[
        [criteria.mapBounds.east, criteria.mapBounds.north],
        [criteria.mapBounds.west, criteria.mapBounds.north],
        [criteria.mapBounds.west, criteria.mapBounds.south],
        [criteria.mapBounds.east, criteria.mapBounds.south],
        [criteria.mapBounds.east, criteria.mapBounds.north],
      ]]
    }
  }];
}

export function deleteCallListItem(id: number) {
  return deleteRequest(`/prospect/call_list_items/${id}`);
}

export function deleteCallListItems(ids: number[]) {
  return post(`/prospect/call_list_items/delete_selected`, { ids });
}

export function fetchCallLists(params: { [key: string]: string }): Promise<CallList[]> {
  return get('/prospect/connect_call_lists', { params });
}

export function fetchCallListItems(params: { [key: string]: string }, id: number): Promise<FetchCallListItemsResponse> {
  const key = Object.keys(params)[0];

  return get(`/prospect/connect_call_lists/${id}/call_list_items?${key}=${params[key]}`);
}

export function getCallListItems(params, id: number):
  Promise<{ total: number, callListItems: CallListItem[], callListItemIds: string[] }> {
  const key = Object.keys(params)[0];

  return get(`/prospect/connect_call_lists/${id}?${key}=${params[key]}`);
}

export function addPropertiesToCallList(callListId: number, insightPropertyIds: string[]) {
  return post(`/prospect/connect_call_lists/${callListId}/add_properties_to_call_list`, { insightPropertyIds });
}

export function updateCallList(
  id: number,
  params: { name: string, description: string, call_list_members: number[] }
) {
  return turboPut(`/prospect/connect_call_lists/${id}`, { connectCallList: params });
}

export function createCallList(name: string, description: string, callListMembers: number[]): Promise<CallList> {
  return post('/prospect/connect_call_lists', { connectCallList: { name, description, callListMembers } });
}

export function getCallConsoleItem(url: string): Promise<GetCallConsoleItemResponse> {
  return get(url);
}

export function fetchImportStatus(importId: number): Promise<CallListImportStatus> {
  return get('/prospect/connect_call_lists/import_status', { params: { importId } });
}

export function createAddressImport(formData): Promise<CreateAddressImportResponse> {
  return fetch('/prospect/connect_call_lists/import', {
    method: 'POST',
    body: formData
  })
    .then(r => r.json());
}

export function fetchCallListExportCreditCounts(callListId: number): Promise<ExportCreditCounts> {
  return get(`/prospect/connect_call_lists/${callListId}/export_credit_counts`);
}

export function addItemToCallList(
  callListId: number,
  propertyId: string,
  callListableType?: string,
  callListableId?: number
): Promise<CallListItem> {
  return post(`/prospect/connect_call_lists/${callListId}/add_call_list_item`, {
    insightsPropertyId: propertyId,
    callListableType,
    callListableId,
  });
}

export function updateConnectProperty(propertyId: string, params) {
  return put(`/prospect/properties/${propertyId}`, { connectProperty: params });
}

export function fetchTags(searchString, limit?): Promise<TagsResponse> {
  return get(
    '/prospect/tags',
    { params: { 'q[name_cont]': searchString, limit } }
  );
}

export function createTag(name: string): Promise<TagResponse> {
  return post('/prospect/tags', { tag: { name } });
}

export function updateTag(id: number, name: string): Promise<TagResponse> {
  return put(`/prospect/tags/${id}`, { tag: { name } });
}

export function addTagToRecord(name: string, recordType?: string, recordId?: number, insightsPropertyId?: string) {
  return post('/prospect/tags/add_to_record', { recordType, recordId, insightsPropertyId, name });
}

export function removeTagFromRecord(name: string, recordType?: string, recordId?: number, insightsPropertyId?: string) {
  return deleteRequest(
    '/prospect/tags/remove_from_record',
    { params: { recordType, recordId, insightsPropertyId, name } }
  );
}

export function getTagsForRecord(recordType?: string, recordId?: number, insightsPropertyId?: string) {
  return get('/prospect/tags/fetch_for_record', { params: { recordType, recordId, insightsPropertyId } });
}

export function deleteTag(tagId: number) {
  return deleteRequest(`/prospect/tags/${tagId}`);
}

export function createExport(exportPath: string, params) {
  return post(exportPath, params);
}

export function createDataExportTemplate(params): Promise<DataExportTemplateResponse> {
  return post('/prospect/data_export_templates', params);
}

export function updateDataExportTemplate(templateId: number, params): Promise<DataExportTemplateResponse> {
  return put(`/prospect/data_export_templates/${templateId}`, params);
}

export function deleteDataExportTemplate(templateId) {
  return deleteRequest(`/prospect/data_export_templates/${templateId}`);
}

export function syncToRethink(
  params: { insights_property_id: string, include_contacts?: boolean }
): Promise<{ property: { rethinkId?: string } }> {
  return post('/prospect/rethink_sync/sync_to_rethink', params);
}

export function updateSearchTableColumns(params) {
  return post('/prospect/search/update_search_table_columns', params);
}

export function fetchRethinkLists(params): Promise<RethinkList[]> {
  return get('/prospect/rethink_sync/fetch_rethink_lists', { params });
}

export function fetchRethinkListSizes(): Promise<{id: string, size: number}[]> {
  return get('/prospect/rethink_sync/fetch_rethink_list_sizes');
}

export function enrichRethinkList(params): Promise<PropertyCrmSync & { listUrl: string }> {
  return post('/prospect/rethink_sync/enrich_rethink_list', params);
}

export function addPropertyToRethinkList(
  params: {
    insightsPropertyId: string;
    includeContacts: boolean;
    rethinkListId?: string;
  }
) {
  return post('/prospect/rethink_sync/add_property_to_list', params);
}

export function createRethinkListWithProperty(
  params: {
    name: string;
    desc: string;
    insightsPropertyId: string;
    includeContacts: boolean;
  }
) {
  return post('/prospect/rethink_sync/create_list_with_property', params);
}

export function createRethinkList(params:
  { name: string; desc: string, bulk: boolean }
): Promise<{ rethinkListId: string }> {
  return post('/prospect/rethink_sync/create_list', params);
}

export function bulkInsertRethinkCallList(params): Promise<PropertyCrmSync> {
  return post('/prospect/rethink_sync/bulk_insert', params);
}

export function getPropertyCrmSync(id: number) {
  return get(`/prospect/property_crm_syncs/${id}`);
}

export function pushContactToRethink(params:
  { insightsPropertyId: string, ownerId: number }): Promise<{ contactRethinkId: string }
  > {
  return post('/prospect/rethink_sync/push_contact', params);
}

export function getCallTemplates(): Promise<CallTemplate[]> {
  return get('/prospect/liquid_templates');
}

export function deleteCallTemplate(id: number) {
  return deleteRequest(`/prospect/liquid_templates/${id}`);
}

export function createCallTemplate(params): Promise<CallTemplate> {
  return post('/prospect/liquid_templates', params);
}

export function updateCallTemplate(params, id: number): Promise<CallTemplate> {
  return patch(`/prospect/liquid_templates/${id}`, params);
}

export function getDemographicsReport(insightsPropertyId: string, radii: string[] = []): Promise<DemographicsReport> {
  return get(`/prospect/off_market_properties/${insightsPropertyId}/demographics`, { params: { radii: radii } });
}

export function getComps(insightsPropertyId, params) {
  const filteredData = Object.keys(params)
    .reduce((obj, key) => {
      obj[`comps_search[${snakeCase(key)}]`] = params[key];
      return obj;
    }, {});

  const queryString = new URLSearchParams(filteredData).toString();
  return get(`/prospect/off_market_properties/${insightsPropertyId}/related_properties?${queryString}`);
}

export function endTrial() {
  return post('/prospect/profile/end_trial', {});
}

export function createFeedback(params): Promise<Feedback> {
  return post('/prospect/feedbacks', params);
}

export function updateFeedback(id: number, params) {
  return put(`/prospect/feedbacks/${id}`, params);
}

export function fetchMailersTemplates(
  params: { page: number } | { [key: string]: string },
  id: number
): Promise<FetchMailersTemplatesResponse> {
  return get(`/prospect/connect_call_lists/${id}/mailers_templates`, { params });
}

export function deleteMailersTemplates(ids: number[]) {
  return post(`/prospect/mailers_templates/destroy_selected`, { ids });
}

export function updateMailersTemplate(callListId: number, id: number, params) {
  return put(`/prospect/connect_call_lists/${callListId}/mailers_templates/${id}`, params);
}

export function createMailersTemplate(callListId: number, title: string): Promise<CreateMailersTemplateResponse> {
  return post(`/prospect/connect_call_lists/${callListId}/mailers_templates`, { mailers_template: { title } });
}

export function previewMailer(
  callListId: number,
  templateId: number,
  queryParams: { item_id?: number, index?: number }
): Promise<PreviewMailerResponse> {
  return get(`/prospect/connect_call_lists/${callListId}/mailers_templates/${templateId}/preview`, {
    params: queryParams
  });
}

export function getMailersHeader(): Promise<MailersHeader> {
  return get(`/prospect/mailers_header`);
}

export function getMailersHeaderPreview(params) {
  return get('/prospect/mailers_header/preview', { params });
}

export function updateMailersHeader(formData) {
  return fetch('/prospect/mailers_header', {
    method: 'POST',
    body: formData
  })
    .then(r => r.json());
}

export function exportAllMailers(callListId: number, params) {
  return post(`/prospect/connect_call_lists/${callListId}/mailers_templates/export_all`, params);
}

export function syncToCrm(
  params: { insights_property_id: string, include_contacts?: boolean }
): Promise<{ id: number }> {
  return post('/prospect/buildout_crm_sync/sync_property_to_crm', params);
}
